// Drift: steamer-appen på Supabase (del af SupabaseApi – se supabase.ts). Alle regler ligger i databasefunktionerne
// (20261028860000_steamer_drift.sql): kun steameren på opgaven må starte, tage billeder, få underskrift og afslutte; efter-billeder
// kræver svar på skader; ekstra tilvalg følger indstillingen "Ude hos kunden"; SMS'er går gennem beskeder (spærren gælder).
// Billederne ligger i den private bucket "steamer-billeder" og vises med midlertidige links.
import { ApiFejl, type Api, type BookingVisning, type RuteStop, type UdlaegVisning } from './api';
import type { Dokumentation, Udlaeg } from '../../core/typer';
import { VINKLER } from '../../core/kundecenter';
import { gyldigDato, rensTekst } from '../../core/validering';
import { iDag } from '../../core/dato';
import { koeretidMin, postnrPunkt, type Punkt } from '../../core/geo';
import { GODKENDELSER, KATEGORIER, tjekUdlaeg, type Aflaesning } from '../../core/udlaeg';
import { tilVisning } from './sbKontor';
import type { SbKontekst } from './sbKatalog';

type R = Record<string, unknown>;
const BUCKET = 'steamer-billeder';
const LAGER = 'lager:';
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
const liste = (l: unknown) => ((l as Parameters<typeof tilVisning>[0][] | null) ?? []).map(tilVisning);
const vinkelNavn = (v: string) => VINKLER.find(([id]) => id === v)?.[1] ?? v;

/** Fast punkt for en adresse: postnummerets midte + en lille fast forskydning pr. nøgle (som i testversionen). */
function punktFor(postnr: string, noegle: number | string): Punkt | null {
  const p = postnrPunkt(postnr); if (!p) return null;
  const h = [...String(noegle)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  return { lat: p.lat + ((h % 100) - 50) / 9000, lng: p.lng + (((h >> 7) % 100) - 50) / 6000 };
}

export class SbSteamer {
  constructor(private readonly c: SbKontekst) {}
  private async rpc<T>(navn: string, args: R = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  /** Kundens sider fra SMS-links: uden login og uden klient – tokenet er adgangen. */
  private async tokenRpc<T>(navn: string, args: R): Promise<T> { return (await this.c.k(this.c.sb.rpc(navn, args))) as T; }
  private async skrivRpc<T>(navn: string, args: R = {}): Promise<T> { const r = await this.rpc<T>(navn, args); this.c.skift(); return r; }

  // ---------- Billeder i lageret ----------
  /** Lægger et billede (data-URL fra kameraet) i lageret under <klient>/<mappe>/ og giver stien tilbage. */
  private async upload(mappe: string, navn: string, billede: string): Promise<string> {
    const m = /^data:(image\/(jpeg|png|webp));base64,/.exec(billede);
    if (!m) throw fejl('Det er ikke et billede.');
    if (billede.length > 6_000_000) throw fejl('Billedet er for stort.');
    const sti = `${await this.c.kraevKlient()}/${mappe}/${navn}-${crypto.randomUUID()}.${m[2] === 'jpeg' ? 'jpg' : m[2]}`;
    const blob = await (await fetch(billede)).blob();
    const { error } = await this.c.sb.storage.from(BUCKET).upload(sti, blob, { contentType: m[1], upsert: false });
    if (error) throw fejl('Billedet kunne ikke gemmes. Tjek forbindelsen, og prøv igen.');
    return sti;
  }
  /** "lager:<sti>" → midlertidigt link (1 time). Andre værdier (fx gamle data-URL'er) gives videre uændret. */
  private async links(vaerdier: (string | null | undefined)[]): Promise<Map<string, string>> {
    const stier = [...new Set(vaerdier.filter((x): x is string => !!x?.startsWith(LAGER)).map((x) => x.slice(LAGER.length)))];
    const ud = new Map<string, string>();
    if (!stier.length) return ud;
    const { data } = await this.c.sb.storage.from(BUCKET).createSignedUrls(stier, 3600);
    for (const x of data ?? []) if (x.path && x.signedUrl) ud.set(LAGER + x.path, x.signedUrl);
    return ud;
  }

  // ---------- Opgaver, rute og status ----------
  minePlaner: Api['minePlaner'] = async (fra, til) => liste(await this.rpc('mine_opgaver', { p_fra: fra, p_til: til }));
  steamerStatus: Api['steamerStatus'] = async (id, status) => { await this.skrivRpc('steamer_status', { p_booking: id, p_status: status }); };
  steamerSms: Api['steamerSms'] = (id, type, minutter) => this.skrivRpc('steamer_sms', { p_booking: id, p_type: type, p_minutter: minutter ?? null });
  rapporterPosition: Api['rapporterPosition'] = (lat, lng) => this.rpc('rapporter_position', { p_lat: lat, p_lng: lng });
  smartRute: Api['smartRute'] = async (dato) => {
    if (!gyldigDato(dato)) throw fejl('Ugyldig dato.');
    const [opgaver, st] = await Promise.all([this.minePlaner(dato, dato),
      this.c.k(this.c.sb.from('steamere').select('id, start_postnr, start_by').eq('klient_id', await this.c.kraevKlient()).limit(1).maybeSingle())]);
    const s = (st as { id: string; start_postnr: string; start_by: string } | null) ?? { id: '', start_postnr: '', start_by: '' };
    const hjem = punktFor(s.start_postnr, s.id);
    let fra: Punkt | null = hjem; let samlet = 0;
    const stop: RuteStop[] = opgaver.filter((b) => ['afventer', 'godkendt', 'i_gang', 'faerdig'].includes(b.status)).map((b) => {
      const p = punktFor(b.postnr, b.id);
      const k = fra && p ? koeretidMin(fra, p) : 0; samlet += k; fra = p ?? fra;
      return { booking: b, koersel: k, ankomst: b.start!, afgang: b.slut!, lat: p?.lat ?? null, lng: p?.lng ?? null };
    });
    const adr = (b: BookingVisning) => encodeURIComponent(`${b.adresse}, ${b.postnr} ${b.by}`);
    const googleLink = stop.length ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${s.start_postnr} ${s.start_by}`)}&destination=${adr(stop[stop.length - 1].booking)}${stop.length > 1 ? `&waypoints=${stop.slice(0, -1).map((x) => adr(x.booking)).join('%7C')}` : ''}&travelmode=driving` : null;
    return { dato, start: { postnr: s.start_postnr, by: s.start_by, lat: hjem?.lat ?? null, lng: hjem?.lng ?? null, tid: stop.length ? stop[0].ankomst - stop[0].koersel : null }, stop, samletKoersel: samlet, googleLink };
  };

  // ---------- Før/efter, skader og underskrift ----------
  gemBillede: Api['gemBillede'] = async (booking_id, type, vinkel, billede, note) => {
    const sti = billede ? await this.upload(String(booking_id), `${type}-${type === 'skade' ? 'skade' : vinkel}`, billede) : '';
    await this.skrivRpc('gem_billede', { p_booking: booking_id, p_type: type, p_vinkel: vinkel, p_sti: sti, p_note: note ?? null });
  };
  skaderFoer: Api['skaderFoer'] = async (id, svar) => { await this.skrivRpc('skader_foer', { p_booking: id, p_svar: svar }); };
  /** Opgavens billeder (steameren: egne opgaver; kontoret: alle) med midlertidige links. */
  dokumentation: Api['dokumentation'] = async (booking_id) => {
    const r = ((await this.c.k(this.c.sb.from('dokumentation').select('id, booking_id, type, vinkel, billede, taget, note')
      .eq('klient_id', await this.c.kraevKlient()).eq('booking_id', booking_id).order('taget'))) as (Dokumentation & { note: string | null })[]).filter((d) => d.billede || d.note);
    const l = await this.links(r.map((d) => d.billede));
    return r.map(({ note, ...d }) => ({ ...d, billede: l.get(d.billede) ?? (d.billede.startsWith(LAGER) ? '' : d.billede), ...(note ? { note } : {}) }));
  };
  /** Kundekortet: steamerens skader før arbejdet på kundens bookinger – billederne med midlertidige links. */
  kundeSkadebilleder: Api['kundeSkadebilleder'] = async (kunde_id) => {
    const r = (await this.rpc<Awaited<ReturnType<Api['kundeSkadebilleder']>> | null>('kunde_skadebilleder', { p_kunde: kunde_id })) ?? [];
    const l = await this.links(r.flatMap((b) => b.billeder.map((x) => x.billede)));
    return r.map((b) => ({ ...b, billeder: b.billeder.map((x) => ({ ...x, billede: l.get(x.billede) ?? (x.billede.startsWith(LAGER) ? '' : x.billede) })) }));
  };
  underskriv: Api['underskriv'] = async (id, d) => {
    if (rensTekst(d.navn, 80).trim().length < 2) throw fejl('Skriv kundens navn.', { navn: 'Skriv kundens navn' });
    await this.skrivRpc('underskriv', { p_booking: id, p_navn: d.navn, p_billede: d.billede });
  };
  fjernGodkendelse: Api['fjernGodkendelse'] = (id) => this.skrivRpc('fjern_godkendelse', { p_booking: id });
  /** Kundens side fra SMS-linket (uden login): billederne får links fra edge-funktionen "steamer-billeder". */
  kvitteringLink: Api['kvitteringLink'] = async (token) => {
    const x = await this.tokenRpc<{ gyldig: boolean; aarsag?: string; godkendt?: string | null; booking?: NonNullable<Awaited<ReturnType<Api['kvitteringLink']>>['booking']>; billeder?: { vinkel: string; foer: string | null; efter: string | null }[] }>('kvittering_link', { p_token: token });
    if (!x.gyldig) return { gyldig: false, aarsag: x.aarsag };
    let links: Record<string, string> = {};
    if ((x.billeder ?? []).some((b) => b.foer?.startsWith(LAGER) || b.efter?.startsWith(LAGER))) {
      try { links = (await this.c.funktion<{ billeder: Record<string, string> }>('steamer-billeder', { token })).billeder ?? {}; } catch { /* billederne vises ikke – resten virker */ }
    }
    const url = (v: string | null) => (!v ? null : v.startsWith(LAGER) ? links[v.slice(LAGER.length)] ?? null : v);
    return { ...x, billeder: (x.billeder ?? []).map((b) => ({ vinkel: vinkelNavn(b.vinkel), foer: url(b.foer), efter: url(b.efter) })) };
  };
  godkendKvittering: Api['godkendKvittering'] = async (token, d) => {
    if (rensTekst(d.navn, 80).trim().length < 2) throw fejl('Skriv dit navn.', { navn: 'Skriv dit navn' });
    await this.tokenRpc('godkend_kvittering', { p_token: token, p_navn: d.navn, p_kommentar: d.kommentar ?? null });
  };

  // ---------- Ekstra tilvalg på stedet ----------
  tilfoejEkstra: Api['tilfoejEkstra'] = (id, tilvalg) => {
    if (!tilvalg.length) throw fejl('Vælg mindst ét tilvalg.');
    return this.skrivRpc('tilfoej_ekstra', { p_booking: id, p_valgte: tilvalg });
  };
  steamerAendrYdelse: Api['steamerAendrYdelse'] = (id, p) => this.skrivRpc('steamer_aendr_ydelse', { p_booking: id, p });
  annullerEkstra: Api['annullerEkstra'] = async (id, ekstra_id) => { await this.skrivRpc('annuller_ekstra', { p_booking: id, p_ekstra: ekstra_id }); };
  ekstraLink: Api['ekstraLink'] = (token) => this.tokenRpc('ekstra_link', { p_token: token });
  svarEkstra: Api['svarEkstra'] = async (token, godkend) => { await this.tokenRpc('svar_ekstra', { p_token: token, p_godkend: godkend }); };

  // ---------- Udlæg fra medarbejderen ----------
  /** Serveren aflæser ikke kvitteringen: browseren læser den (OCR), medarbejderen tjekker/retter tallene, og kontoret ser billedet. */
  laesKvittering: Api['laesKvittering'] = async (billede) => {
    if (!/^data:image\//.test(billede)) throw fejl('Det er ikke et billede.');
    if (billede.length > 4_000_000) throw fejl('Billedet er for stort.');
    return { er_kvittering: true, butik: null, dato: iDag(), total: null, moms: null, valuta: 'DKK', kategori: 'andet', sikkerhed: 0, kilde: 'manuel' };
  };
  private async udlaegMappe() { return `udlaeg/${await this.rpc<string>('min_profil')}`; }
  sendUdlaeg: Api['sendUdlaeg'] = async (d) => {
    const formaal = rensTekst(d.formaal ?? '', 300).trim(), soegt = Math.round(Number(d.soegt));
    const f: Record<string, string> = {};
    if (formaal.length < 3) f.formaal = 'Skriv hvad udlægget var til';
    if (!(soegt > 0) || soegt > 2_000_000) f.soegt = 'Skriv beløbet';
    if (!KATEGORIER.some(([k]) => k === d.kategori)) f.kategori = 'Vælg en kategori';
    if (Object.keys(f).length) throw fejl('Udfyld de markerede felter.', f);
    if (!d.billede) throw fejl('Tag et billede af kvitteringen.');
    if (!d.aflaest) throw fejl('Lad Freemi læse kvitteringen først.');
    const aflaest: Aflaesning = { ...d.aflaest, kilde: 'manuel', sikkerhed: 0, total: d.aflaest.total ?? soegt };
    const sti = await this.upload(await this.udlaegMappe(), 'kvittering', d.billede);
    const id = await this.skrivRpc<number>('send_udlaeg', { p: { formaal, soegt, kategori: d.kategori, sti, aflaest, rettet: !!d.rettet, booking_id: d.booking_id ?? null } });
    return { id: Number(id), tjek: tjekUdlaeg(soegt, aflaest) };
  };
  mineUdlaeg: Api['mineUdlaeg'] = async () => {
    const l = (await this.rpc<(Udlaeg & { navn: string; rolle: UdlaegVisning['rolle'] })[] | null>('mine_udlaeg')) ?? [];
    const links = await this.links(l.map((u) => u.billede));
    return l.map((u) => ({
      ...u, billede: u.billede ? links.get(u.billede) ?? null : null, egen: true, harGodkendt: false, kanGodkende: false, anmodning: null,
      mangler: Math.max(0, GODKENDELSER - (u.godkendelser?.length ?? 0)), tjek: tjekUdlaeg(u.soegt, u.aflaest),
    }));
  };
  svarUdlaeg: Api['svarUdlaeg'] = async (id, svar) => {
    if (rensTekst(svar ?? '', 500).trim().length < 2) throw fejl('Skriv dit svar.');
    await this.skrivRpc('svar_udlaeg', { p_id: id, p_svar: svar });
  };
  mitUdlaegKvittering: Api['mitUdlaegKvittering'] = async (id, billede, aflaest) => {
    const a: Aflaesning = { ...aflaest, kilde: 'manuel', sikkerhed: 0 };
    const sti = await this.upload(await this.udlaegMappe(), 'kvittering', billede);
    await this.skrivRpc('mit_udlaeg_kvittering', { p_id: id, p_sti: sti, p_aflaest: a });
    const u = (await this.mineUdlaeg()).find((x) => x.id === id);
    return { tjek: u ? u.tjek : tjekUdlaeg(0, a) };
  };
}
